import { aabb2ByPolyLine2s, aabb2Grow, aabb2Overlaps } from "../aabb2";
import { brepAABB3, faceArea, transformFaceLoopBasis, type BRep, type Face, faceNew, faceReverse } from "../brep";
import { intersectFacePlane } from "../intersect";
import { IntersectionKind } from "../intersectRay2";
import { line2IsCoincident } from "../line2";
import { planeIsCodirectional, planeIsCoplanar, planeNewXY } from "../plane";
import { polyLine2Copy } from "../polyLine2";
import type { PolyLine2 } from "../polyLine2Types";
import { brepNeedsOutwardNormalFlip, faceFragmentBelongsInIntersection } from "./classifyPointRelativeToBRep";
import type { DCEL, Segment } from "./dcel";
import { dcelFromPolygonsSnapRound, DEFAULT_SNAP_ROUND_PRECISION, oppositeHalfEdgeIndex } from "./dcelFromPolygons";
import { setContains, setOpByPredicatePolygons } from "./setOps";
import { faceToPolygon, polygonToFace } from "./trimFaceWithPlane";

/**
 * Drop near-zero / snap-debris faces that corrupt signed-volume orientation.
 * Must stay below contact-face areas from thin probe solids (e.g. half-story roof-clip
 * gate ribbons at TINY_PLANE_THICKNESS = 0.001", which yield ~0.002 sq in scraps).
 */
const MIN_INTERSECT_FACE_AREA = 1e-3;

function shouldAddFacePlaneIntersectionCuts(
  result: ReturnType<typeof intersectFacePlane>,
): result is NonNullable<ReturnType<typeof intersectFacePlane>> {
  // TRANSVERSE chords only. Edge-on TANGENT cuts are not fed into INTERSECT — room∩floor
  // micro-insets are removed upstream by snapping floor/roof outlines to room outlines.
  return !!result && result.kind === IntersectionKind.TRANSVERSE && result.segments.length > 0;
}

/**
 * Drop duplicate chords in either orientation. Opposite cuts appear when a T-junction
 * splits one wall into two coplanar faces that both contribute the shared edge-on
 * contact to the opposing arrangement.
 */
function dedupeIntersectionSegments(segments: readonly Segment[]): Segment[] {
  const unique: Segment[] = [];
  for (const segment of segments) {
    if (
      unique.some((existing) =>
        line2IsCoincident(
          existing[0],
          existing[1],
          existing[2],
          existing[3],
          segment[0],
          segment[1],
          segment[2],
          segment[3],
        ),
      )
    ) {
      continue;
    }
    unique.push(segment);
  }
  return unique;
}

const GROW_AABB2: number = 0.1; //use big tolerance for now

/**
 * Identifies faces from the first operand in the set operation
 */
const FIRST_OPERAND_ID = 0;

/**
 * Identifies faces from the second operand in the set operation
 */
const SECOND_OPERAND_ID = 1;

/**
 * Identifies intersection segments. These come from intersections
 * of the face plane with faces from the opposing operand.
 */
const INTERSECTION_SEGMENT_ID = 2;

type IndexedFace = Face & { index: number };

type IndexedFaceBRep = Omit<BRep, "faces"> & { faces: IndexedFace[] };

function indexedFaceNew(face: Face, index: number): IndexedFace {
  return { ...face, index };
}

export type FacePlaneIntersection =
  | {
      type: "COPLANAR";
      aIndex: number;
      bIndex: number;
      oppFace: IndexedFace;
    }
  | {
      type: "TRANSVERSE";
      aIndex: number;
      bIndex: number;
      segments: Segment[];
    };

export interface BRepIntersectAllResult {
  faceIsects: FacePlaneIntersection[][];
  oppFaceIsects: FacePlaneIntersection[][];
}

export interface BRepFacePairContext {
  readonly aFace: Face;
  readonly bFace: Face;
  readonly aIndex: number;
  readonly bIndex: number;
  readonly transformedBFaceLoopsInAPlane: PolyLine2[];
  readonly codirectional: boolean;
}

export interface BRepIntersectAllOptions {
  readonly onFacePair?: (ctx: BRepFacePairContext) => void;
}

export interface IntersectBRepWithProvenanceResult {
  readonly faces: Face[];
  readonly pairToResultFaceIndices: Map<string, number[]>;
}

export function mapBRepToIndexedFaceBRep(
  brep: BRep,
  startIndex: number = 0,
): { indexedFaceBRep: IndexedFaceBRep; lastIndex: number } {
  const indexedFaceBRep: IndexedFaceBRep = {
    ...brep,
    faces: brep.faces.map((face) => ({ ...face, index: startIndex++ })),
  };

  return { indexedFaceBRep, lastIndex: startIndex };
}

export function brepsIntersectAll(
  a: IndexedFaceBRep,
  b: IndexedFaceBRep,
  opType: BRepBooleanOp,
  opts?: BRepIntersectAllOptions,
): BRepIntersectAllResult {
  const faceIsects: FacePlaneIntersection[][] = [];
  const oppFaceIsects: FacePlaneIntersection[][] = [];

  function addAFaceIsect(i: FacePlaneIntersection) {
    if (!faceIsects[i.aIndex]) {
      faceIsects[i.aIndex] = [];
    }

    faceIsects[i.aIndex].push(i);
  }

  function addBFaceIsect(i: FacePlaneIntersection) {
    if (!oppFaceIsects[i.aIndex]) {
      oppFaceIsects[i.aIndex] = [];
    }

    oppFaceIsects[i.aIndex].push(i);
  }

  // TODO
  // const aTree = rtreeBRepFaces(a);

  for (let i = 0; i < a.faces.length; i++) {
    const aFace = a.faces[i];

    const aabb2AFace = aabb2ByPolyLine2s(aFace.loops);

    if (!aabb2AFace) {
      continue;
    }

    aabb2Grow(aabb2AFace, GROW_AABB2, GROW_AABB2);

    for (let j = 0; j < b.faces.length; j++) {
      const bFace = b.faces[j];

      if (planeIsCoplanar(aFace.plane, bFace.plane)) {
        const transformedBFaceLoop = bFace.loops.map((loop) =>
          transformFaceLoopBasis(polyLine2Copy(loop), bFace.plane, aFace.plane),
        );

        const aabb2BFace = aabb2ByPolyLine2s(transformedBFaceLoop);

        if (!aabb2BFace) {
          continue;
        }

        aabb2Grow(aabb2BFace, GROW_AABB2, GROW_AABB2);

        if (!aabb2Overlaps(aabb2AFace, aabb2BFace)) {
          continue; //if they don't overlap, there's no intersection
        }

        const codirectional = planeIsCodirectional(aFace.plane, bFace.plane);
        const onFacePair = opts?.onFacePair;
        if (onFacePair) {
          onFacePair({
            aFace,
            bFace,
            aIndex: i,
            bIndex: j,
            transformedBFaceLoopsInAPlane: transformedBFaceLoop,
            codirectional,
          });
        }

        const transformedAFaceLoop = aFace.loops.map((loop) =>
          transformFaceLoopBasis(polyLine2Copy(loop), aFace.plane, bFace.plane),
        );

        // There are two cases of coplanarity:
        // - the face normals have the same direction (called "codirectional")
        // - the face normals have opposite directions

        if (!codirectional) {
          // If the two faces are not codirectional and we're performing a union, we can convert
          // the second operand to a transverse cut (as if the face were pushed into the opposing solid)
          if (opType === "UNION") {
            addAFaceIsect({
              type: "TRANSVERSE",
              aIndex: i,
              bIndex: j,
              segments: convertFaceToSegments(faceNew(transformedBFaceLoop, planeNewXY())),
            });

            addBFaceIsect({
              type: "TRANSVERSE",
              aIndex: j,
              bIndex: i,
              segments: convertFaceToSegments(faceNew(transformedAFaceLoop, planeNewXY())),
            });

            continue;
          } else {
            // If the two faces are not codirectional and we're performing a subtraction or intersection, we ignore the
            // intersection as if the two do not intersect. This prevents infinitely thin solids in the intersection
            // case and missing faces in the subtraction case.
            continue;
          }
        }

        // If we are dealing with codirectional faces, we can transfer the face basis into the opposing face
        // and add the intersection to the result.
        addAFaceIsect({
          type: "COPLANAR",
          aIndex: i,
          bIndex: j,
          oppFace: indexedFaceNew(faceNew(transformedBFaceLoop, planeNewXY()), bFace.index),
        });

        addBFaceIsect({
          type: "COPLANAR",
          aIndex: j,
          bIndex: i,
          oppFace: indexedFaceNew(faceNew(transformedAFaceLoop, planeNewXY()), aFace.index),
        });

        continue;
      }

      {
        const result = intersectFacePlane(bFace, aFace.plane, []);

        if (shouldAddFacePlaneIntersectionCuts(result)) {
          addAFaceIsect({
            type: "TRANSVERSE",
            aIndex: i,
            bIndex: j,
            segments: result.segments.map((x) => [x.planeU1, x.planeV1, x.planeU0, x.planeV0] as Segment),
          });
        }
      }

      {
        const result = intersectFacePlane(aFace, bFace.plane, []);

        if (shouldAddFacePlaneIntersectionCuts(result)) {
          addBFaceIsect({
            type: "TRANSVERSE",
            aIndex: j,
            bIndex: i,
            segments: result.segments.map((x) => [x.planeU1, x.planeV1, x.planeU0, x.planeV0] as Segment),
          });
        }
      }
    }
  }

  return {
    faceIsects,
    oppFaceIsects,
  };
}

function convertFaceToSegments(face: Face): Segment[] {
  const segments: Segment[] = [];

  for (const loop of face.loops) {
    const loopSegments = loopToSegments(loop);
    segments.push(...loopSegments);
  }

  return segments;
}

function loopToSegments(loop: PolyLine2): Segment[] {
  const segments: Segment[] = [];

  // Closed PolyLine2 is [x0,y0,...,x0,y0]; each edge reads four floats ending at i+3.
  for (let i = 0; i < loop.length - 2; i += 2) {
    const x1 = loop[i];
    const y1 = loop[i + 1];
    const x2 = loop[i + 2];
    const y2 = loop[i + 3];
    segments.push([x1, y1, x2, y2] as Segment);
  }

  return segments;
}

export function unionBRep(a: BRep, b: BRep) {
  const { indexedFaceBRep: aIndexed, lastIndex: aLastIndex } = mapBRepToIndexedFaceBRep(a);
  const { indexedFaceBRep: bIndexed } = mapBRepToIndexedFaceBRep(b, aLastIndex);
  const intersectAllResult = brepsIntersectAll(aIndexed, bIndexed, "UNION");

  const builtFaceDCELs = dcelsFromIntersectionResult(aIndexed, bIndexed, intersectAllResult);

  // future optimizations
  // - faces with no intersection require no dcel (we can classify them as inside or outside)
  // - coplanar faces are symmetric, there's no reason to build multiple dcels for them

  const faces: Face[] = [];

  const operandIds = [FIRST_OPERAND_ID, SECOND_OPERAND_ID];

  function pred(dcel: DCEL | "NO_ISECT", originalFace: Face) {
    if (dcel === "NO_ISECT") {
      faces.push(originalFace);
      return;
    }

    const polys = setOpByPredicatePolygons(dcel, (halfEdgeIndex) => {
      const face = dcel.halfEdgeToFace[halfEdgeIndex];
      const polygons = dcel.faceToPolygons[face];

      const opp = oppositeHalfEdgeIndex(halfEdgeIndex);
      const oppFace = dcel.halfEdgeToFace[opp];
      const oppPolygons = dcel.faceToPolygons[oppFace];

      if (setContains(polygons, operandIds) && !setContains(polygons, [INTERSECTION_SEGMENT_ID])) {
        if (!setContains(oppPolygons, operandIds) || setContains(oppPolygons, [INTERSECTION_SEGMENT_ID])) {
          return true;
        }
      }

      return false;
    });

    faces.push(...polys.map((poly) => polygonToFace(poly, originalFace.plane)));
  }

  for (const { dcel, originalFace } of builtFaceDCELs) {
    pred(dcel, originalFace);
  }

  return faces;
}

function intersectPairKey(aFaceIndex: number, bFaceIndex: number): string {
  return `${aFaceIndex}:${bFaceIndex}`;
}

/**
 * INTERSECT face retention (Open CASCADE / Requicha pattern):
 * 1. Split each source face by face–face cuts (DCEL).
 * 2. Extract fragments that belong to the source face (FIRST).
 * 3. Classify each fragment vs the opposing solid via an inward offset sample.
 * 4. Keep when that sample is IN the opponent (regularized COMMON). Also keep
 *    uncut faces whose inward sample is IN the opponent (Face/Solid).
 */
function buildIntersectFacesAndSourceMap(
  builtFaceDCELs: BuiltFaceDCEL[],
  a: BRep,
  b: BRep,
  bFaceIsects: FacePlaneIntersection[][],
): {
  faces: Face[];
  resultFaceIndicesBySourceFaceGlobalIndex: Map<number, number[]>;
} {
  const faces: Face[] = [];
  const resultFaceIndicesBySourceFaceGlobalIndex = new Map<number, number[]>();

  // Orientations and AABBs are stable for the operands — compute once, reuse per fragment.
  const aNeedsOutwardNormalFlip = brepNeedsOutwardNormalFlip(a);
  const bNeedsOutwardNormalFlip = brepNeedsOutwardNormalFlip(b);
  const aAabb = brepAABB3(a);
  const bAabb = brepAABB3(b);

  function appendResultFace(resultFace: Face, sourceFaceGlobalIndex: number): void {
    if (Math.abs(faceArea(resultFace)) < MIN_INTERSECT_FACE_AREA) {
      return;
    }
    const resultFaceIndex = faces.length;
    faces.push(resultFace);
    const resultFaceIndices = resultFaceIndicesBySourceFaceGlobalIndex.get(sourceFaceGlobalIndex) ?? [];
    resultFaceIndices.push(resultFaceIndex);
    resultFaceIndicesBySourceFaceGlobalIndex.set(sourceFaceGlobalIndex, resultFaceIndices);
  }

  function pred(dcel: DCEL | "NO_ISECT", originalFace: Face, isA: boolean, localFaceIndex: number) {
    const opposingBRep = isA ? b : a;
    const classifyContext = isA
      ? { sourceNeedsOutwardNormalFlip: aNeedsOutwardNormalFlip, opposingAabb: bAabb }
      : { sourceNeedsOutwardNormalFlip: bNeedsOutwardNormalFlip, opposingAabb: aAabb };
    const sourceFace = originalFace as IndexedFace;
    const sourceFaceGlobalIndex = sourceFace.index;

    // Codirectional coplanar overlap is extracted from both operand DCELs. Emit those
    // faces once from A. Only skip a B face when brepsIntersectAll recorded a COPLANAR
    // pair for this specific face (AABB-overlap gated) — not merely because some distant
    // A face shares the same plane.
    if (!isA) {
      const recordedIsects = bFaceIsects[localFaceIndex];
      if (recordedIsects?.some((isect) => isect.type === "COPLANAR")) {
        return;
      }
    }

    if (dcel === "NO_ISECT") {
      if (faceFragmentBelongsInIntersection(originalFace, opposingBRep, classifyContext)) {
        appendResultFace(originalFace, sourceFaceGlobalIndex);
      }
      return;
    }

    // Candidate fragments from the arrangement: cut-adjacent (FIRST∧ISECT_SEG) or
    // coplanar overlap (FIRST∧SECOND). Solid classification below drops OUT leftovers
    // (e.g. through-slab wall above the plate). We intentionally do not take the full
    // FIRST boundary — that merges multiple FIRST regions into one polygon.
    const candidatePolys = setOpByPredicatePolygons(dcel, (halfEdgeIndex) => {
      const face = dcel.halfEdgeToFace[halfEdgeIndex];
      const polygons = dcel.faceToPolygons[face];

      if (setContains(polygons, [FIRST_OPERAND_ID]) && setContains(polygons, [INTERSECTION_SEGMENT_ID])) {
        return true;
      }

      if (setContains(polygons, [FIRST_OPERAND_ID]) && setContains(polygons, [SECOND_OPERAND_ID])) {
        return true;
      }

      return false;
    });

    for (const poly of candidatePolys) {
      const resultFace = polygonToFace(poly, originalFace.plane);
      // Drop snap debris before membership classification — faceFragmentBelongsInIntersection
      // can throw on degenerate DCEL slivers that MIN_INTERSECT_FACE_AREA is meant to discard.
      if (Math.abs(faceArea(resultFace)) < MIN_INTERSECT_FACE_AREA) {
        continue;
      }
      if (!faceFragmentBelongsInIntersection(resultFace, opposingBRep, classifyContext)) {
        continue;
      }
      appendResultFace(resultFace, sourceFaceGlobalIndex);
    }
  }

  for (const { dcel, originalFace, isA, index } of builtFaceDCELs) {
    pred(dcel, originalFace, isA, index);
  }

  return { faces, resultFaceIndicesBySourceFaceGlobalIndex };
}

function intersectBRepCore(
  a: BRep,
  b: BRep,
  opts?: BRepIntersectAllOptions,
): {
  faces: Face[];
  intersectAllResult: BRepIntersectAllResult;
  resultFaceIndicesBySourceFaceGlobalIndex: Map<number, number[]>;
  aFaceCount: number;
} {
  const { indexedFaceBRep: aIndexed, lastIndex: aLastIndex } = mapBRepToIndexedFaceBRep(a);
  const { indexedFaceBRep: bIndexed } = mapBRepToIndexedFaceBRep(b, aLastIndex);
  const intersectAllResult = brepsIntersectAll(aIndexed, bIndexed, "INTERSECT", opts);
  const builtFaceDCELs = dcelsFromIntersectionResult(aIndexed, bIndexed, intersectAllResult);
  const { faces, resultFaceIndicesBySourceFaceGlobalIndex } = buildIntersectFacesAndSourceMap(
    builtFaceDCELs,
    a,
    b,
    intersectAllResult.oppFaceIsects,
  );
  return {
    faces,
    intersectAllResult,
    resultFaceIndicesBySourceFaceGlobalIndex,
    aFaceCount: aIndexed.faces.length,
  };
}

// Building-graph consumers need to attribute face edges back to source face pairs.
// A single input pair can split into multiple output faces in DCEL boolean extraction,
// so geometric rematching is expensive and can be ambiguous. This returns provenance
// directly from the boolean pipeline as pair -> resultFaceIndices.
export function intersectBRepWithProvenance(
  a: BRep,
  b: BRep,
  opts?: BRepIntersectAllOptions,
): IntersectBRepWithProvenanceResult {
  const { faces, intersectAllResult, resultFaceIndicesBySourceFaceGlobalIndex, aFaceCount } = intersectBRepCore(
    a,
    b,
    opts,
  );

  const encounteredPairs = new Set<string>();
  for (let aFaceIndex = 0; aFaceIndex < intersectAllResult.faceIsects.length; aFaceIndex++) {
    const intersections = intersectAllResult.faceIsects[aFaceIndex];
    if (!intersections) {
      continue;
    }
    for (const intersection of intersections) {
      encounteredPairs.add(intersectPairKey(aFaceIndex, intersection.bIndex));
    }
  }

  const pairToResultFaceIndices = new Map<string, number[]>();
  for (const pairKey of encounteredPairs) {
    const [aFaceIndexString, bFaceIndexString] = pairKey.split(":");
    const aFaceIndex = Number(aFaceIndexString);
    const bFaceIndex = Number(bFaceIndexString);
    if (!Number.isFinite(aFaceIndex) || !Number.isFinite(bFaceIndex)) {
      continue;
    }
    const aGlobalFaceIndex = aFaceIndex;
    const bGlobalFaceIndex = aFaceCount + bFaceIndex;
    const aResults = resultFaceIndicesBySourceFaceGlobalIndex.get(aGlobalFaceIndex) ?? [];
    const bResults = resultFaceIndicesBySourceFaceGlobalIndex.get(bGlobalFaceIndex) ?? [];
    const pairResultFaceIndices = [...new Set([...aResults, ...bResults])];
    if (pairResultFaceIndices.length === 0) {
      continue;
    }
    pairToResultFaceIndices.set(pairKey, pairResultFaceIndices);
  }

  return { faces, pairToResultFaceIndices };
}

export function intersectBRep(a: BRep, b: BRep, opts?: BRepIntersectAllOptions) {
  const result = intersectBRepCore(a, b, opts);
  return result.faces;
}

export function subtractBRep(a: BRep, b: BRep) {
  const { indexedFaceBRep: aIndexed, lastIndex: aLastIndex } = mapBRepToIndexedFaceBRep(a);
  const { indexedFaceBRep: bIndexed } = mapBRepToIndexedFaceBRep(b, aLastIndex);
  const intersectAllResult = brepsIntersectAll(aIndexed, bIndexed, "SUBTRACT");
  const builtFaceDCELs = dcelsFromIntersectionResult(aIndexed, bIndexed, intersectAllResult);

  const faces: Face[] = [];

  function pred(dcel: DCEL | "NO_ISECT", originalFace: Face, isA: boolean) {
    // Faces that don't intersect b are part of the result and require no further processing
    if (dcel === "NO_ISECT") {
      if (isA) {
        faces.push(originalFace);
      }
      return;
    }

    // Look through the dcel for faces that shoul dbe included in the result
    const polys = setOpByPredicatePolygons(dcel, (halfEdgeIndex) => {
      const face = dcel.halfEdgeToFace[halfEdgeIndex];
      const polygons = dcel.faceToPolygons[face];

      if (isA) {
        const face = dcel.halfEdgeToFace[halfEdgeIndex];
        const polygons = dcel.faceToPolygons[face];

        if (
          setContains(polygons, [FIRST_OPERAND_ID]) &&
          !setContains(polygons, [INTERSECTION_SEGMENT_ID]) &&
          !setContains(polygons, [SECOND_OPERAND_ID])
        ) {
          return true;
        }

        return false;
      } else {
        if (
          setContains(polygons, [SECOND_OPERAND_ID]) &&
          !setContains(polygons, [INTERSECTION_SEGMENT_ID]) &&
          !setContains(polygons, [FIRST_OPERAND_ID])
        ) {
          return true;
        }

        if (setContains(polygons, [FIRST_OPERAND_ID]) && setContains(polygons, [INTERSECTION_SEGMENT_ID])) {
          return true;
        }

        return false;
      }
    });

    const resultFaces = polys.map((poly) => polygonToFace(poly, originalFace.plane));

    // The faces from B are oppositely oriented in the result
    if (!isA) {
      resultFaces.forEach((f) => faceReverse(f));
    }

    faces.push(...resultFaces);
  }

  for (const { dcel, originalFace, isA } of builtFaceDCELs) {
    pred(dcel, originalFace, isA);
  }

  return faces;
}

export type BRepBooleanOp = "UNION" | "SUBTRACT" | "INTERSECT";

export function booleanBRep(a: BRep, b: BRep, op: BRepBooleanOp) {
  if (op === "UNION") {
    return unionBRep(a, b);
  } else if (op === "INTERSECT") {
    return intersectBRep(a, b);
  } else {
    return subtractBRep(a, b);
  }
}

export interface BuiltFaceDCEL {
  readonly originalFace: IndexedFace;
  readonly isA: boolean;
  readonly dcel: DCEL | "NO_ISECT";
  readonly index: number;
}

function createSymmetricHashFromIndexArray(arrIndex: number[]): string {
  const sortedArr = [...arrIndex].sort((a, b) => a - b);
  return sortedArr.join("_");
}

export function dcelsFromIntersectionResult(
  a: IndexedFaceBRep,
  b: IndexedFaceBRep,
  intersectAllResult: BRepIntersectAllResult,
): BuiltFaceDCEL[] {
  const coplanarFaceDCEL: Map<string, DCEL> = new Map();
  const builtFaceDCELs: BuiltFaceDCEL[] = [];

  for (let i = 0; i < a.faces.length; i++) {
    const computedDCEL = buildFaceDCEL(a.faces[i], intersectAllResult.faceIsects[i], coplanarFaceDCEL, i);
    if (computedDCEL) {
      builtFaceDCELs.push(computedDCEL);
    }
  }

  for (let i = 0; i < b.faces.length; i++) {
    const computedDCEL = buildFaceDCEL(b.faces[i], intersectAllResult.oppFaceIsects[i], coplanarFaceDCEL, i, false);
    if (computedDCEL) {
      builtFaceDCELs.push(computedDCEL);
    }
  }

  return builtFaceDCELs;
}

function buildFaceDCEL(
  face: IndexedFace,
  intersections: FacePlaneIntersection[] | undefined,
  coplanarFaceDCEL: Map<string, DCEL>,
  index: number,
  isA: boolean = true,
): BuiltFaceDCEL | null {
  if (!intersections || intersections.length === 0) {
    return { originalFace: face, dcel: "NO_ISECT", isA, index };
  }
  const { coplanarFaces, intersectionSegments } = buildFaceDCELInput(face, intersections);

  const coplanarFaceHash = createSymmetricHashFromIndexArray(coplanarFaces.map((x) => x.index));

  if (coplanarFaceDCEL.has(coplanarFaceHash)) {
    return null;
  }

  const newDCEL = dcelFromPolygonsSnapRound({
    polygons: coplanarFaces.map((face) => faceToPolygon(face, true)),
    // `coplanarFaces` is `[face, ...mergedCoplanarPartners]`. All merged partners represent the
    // same "opposing operand" role regardless of how many there are, so they all share
    // SECOND_OPERAND_ID rather than each getting its own array-index id — otherwise a third (or
    // later) partner's index would collide with INTERSECTION_SEGMENT_ID below and corrupt the
    // DCEL's polygon membership for the other, genuinely overlapping partner.
    polygonIds: coplanarFaces.map((_, i) => (i === 0 ? FIRST_OPERAND_ID : SECOND_OPERAND_ID)),
    segments: intersectionSegments,
    segmentPolygonIds: intersectionSegments.map((_) => INTERSECTION_SEGMENT_ID),
    snapRoundPrecision: DEFAULT_SNAP_ROUND_PRECISION,
  });

  // cache the DCEL using global face index
  coplanarFaceDCEL.set(coplanarFaceHash, newDCEL);

  return { originalFace: face, dcel: newDCEL, isA, index };
}

export function buildFaceDCELInput(face: IndexedFace, intersections: FacePlaneIntersection[]) {
  const coplanarFaces: IndexedFace[] = [face];
  const intersectionSegments: Segment[] = [];

  for (const isect of intersections) {
    if (isect.type === "COPLANAR") {
      // if the faces are coplanar, we should just merge them
      coplanarFaces.push(isect.oppFace);
    } else {
      // if the faces are not coplanar, we need to build a dcel
      intersectionSegments.push(...isect.segments);
    }
  }

  return { coplanarFaces, intersectionSegments: dedupeIntersectionSegments(intersectionSegments) };
}
